import React, { Component, ReactNode } from 'react';
import Editor from '@monaco-editor/react';
import { RootState } from '../../../redux/store';
import { connect, ConnectedProps } from 'react-redux';
import { changeBasic, clearBasic } from '../../../redux/actions/BasicActions';
import DataTypeSelectBoxComponent from '../../custom/DataTypeSelectBoxComponent';
import FormicaInput from '../../custom/FormicaInput';
import FormicaLabel from '../../custom/FormicaLabel';
import RoleHelper from '../../../RoleHelper';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { BasicState } from '../../../redux/reducers/BasicReducer';
import DataType from '../../../constant/DataType';
import CustomLoading from '../../custom/CustomLoading';
import { DateVariableProperties } from '../../../domain/datatype/VariablePropertiesDomains';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';

class BasicEditorComponent extends Component<Props> {
  public render(): ReactNode {
    const {
      basic: { basicVariable, dependencies },
    } = this.props;
    if (basicVariable == undefined)
      return (
        <>
          <CustomLoading />
        </>
      );
    return (
      <div className={'grid grid-cols-5 w-full h-full'}>
        <div className={'col-span-1 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-2'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.dataType', defaultMessage: 'Data Type' })} />
          <DataTypeSelectBoxComponent
            disabled={!RoleHelper.canEdit('EnrichmentBasic') || dependencies?.length > 0}
            disabledSelect={!RoleHelper.canEdit('EnrichmentBasic') || dependencies?.length > 0}
            onChangeValue={this.onDataTypeChange}
            properties={basicVariable.properties}
            data-testid={`${ComponentName.BASIC_VARIABLE}_data_type_select_box`}
          />
          <div className={'flex w-full'}>
            {basicVariable.properties?.dataType == 'Date' && (
              <div className={'flex flex-col gap-x-1 w-full'}>
                <FormicaLabel label={LanguageUtil.get({ id: 'common.label.dateFormat', defaultMessage: 'Date Format' })} />
                <FormicaInput
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.dateFormat', defaultMessage: 'Date Format' })}
                  label={'Date Format'}
                  disabled={!RoleHelper.canEdit('EnrichmentBasic')}
                  value={(basicVariable.properties as DateVariableProperties).format}
                  onChange={this.onChangeDateFormat}
                  data-testid={`${ComponentName.BASIC_VARIABLE}_date_format_input`}
                />
              </div>
            )}
          </div>
        </div>
        <div className={'col-span-4 flex flex-col w-full h-full px-5 py-5 gap-y-2'}>
          <FormicaLabel label={'Value'} />
          <Editor
            onChange={(e) => this.onDataValueChange(e as string)}
            options={{
              readOnly: !RoleHelper.canEdit('EnrichmentBasic'),
            }}
            key={basicVariable.id}
            value={basicVariable.value as any}
            className={'bg-[#E0E4E8] basic-variable-value'}
            defaultLanguage={this.getEditorType()}
            data-testid={`${ComponentName.BASIC_VARIABLE}_value_editor`}
          />
        </div>
      </div>
    );
  }

  private onDataTypeChange = (e: FormicaSelectBoxItem) => {
    const {
      basic: { basicVariable },
      changeBasic,
    } = this.props;
    basicVariable.properties = e?.properties || { dataType: DataType.STRING };
    this.setState({ selectedProperties: e.properties });
    changeBasic(basicVariable);
  };

  private onDataValueChange = (e: string) => {
    const {
      basic: { basicVariable },
      changeBasic,
    } = this.props;
    if (!RoleHelper.canEdit('EnrichmentBasic')) return;
    basicVariable.value = e;
    changeBasic(basicVariable);
  };

  private onChangeDateFormat = (e: any) => {
    const {
      basic: { basicVariable },
      changeBasic,
    } = this.props;
    (basicVariable.properties as DateVariableProperties).format = e.currentTarget.value;
    changeBasic(basicVariable);
  };

  private getEditorType = () => {
    const {
      basic: { basicVariable },
    } = this.props;
    switch (basicVariable.properties?.dataType) {
      case 'Object':
      case 'Array':
        return 'json';
      default:
        return 'text';
    }
  };
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  basic: state.basic as BasicState,
  isUpdate: state.basic.isUpdate,
});

const mapDispatchToProps = { changeBasic, clearBasic };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(BasicEditorComponent);
